import { DateTime } from "luxon"

import { DateFilterValue } from "@/components/reservations/date_filter"
import i18n from "@/i18n"

export type PresetType =
  | "today"
  | "yesterday"
  | "last_7_days"
  | "last_30_days"
  | "this_month"
  | "last_month"
  | "custom_range"

export const getPresetDates = (preset: PresetType): { from: DateTime; to: DateTime } => {
  const today = DateTime.now().startOf("day")

  switch (preset) {
    case "today":
      return { from: today, to: today }
    case "yesterday": {
      const yesterday = today.minus({ days: 1 })
      return { from: yesterday, to: yesterday }
    }
    case "last_7_days":
      return { from: today.minus({ days: 6 }), to: today }
    case "last_30_days":
      return { from: today.minus({ days: 29 }), to: today }
    case "this_month":
      return { from: today.startOf("month"), to: today.endOf("month") }
    case "last_month": {
      const lastMonth = today.minus({ months: 1 })
      return { from: lastMonth.startOf("month"), to: lastMonth.endOf("month") }
    }
    case "custom_range":
    default:
      return { from: today, to: today }
  }
}

export const detectPreset = (from: DateTime | null, to: DateTime | null): PresetType => {
  if (!from || !to) return "custom_range"

  const today = DateTime.now().startOf("day")
  const fromNorm = from.startOf("day")
  const toNorm = to.startOf("day")

  // Compare by milliseconds to avoid locale differences
  if (+fromNorm === +today && +toNorm === +today) {
    return "today"
  }

  const yesterday = today.minus({ days: 1 })
  if (+fromNorm === +yesterday && +toNorm === +yesterday) {
    return "yesterday"
  }

  const last7From = today.minus({ days: 6 })
  if (+fromNorm === +last7From && +toNorm === +today) {
    return "last_7_days"
  }

  const last30From = today.minus({ days: 29 })
  if (+fromNorm === +last30From && +toNorm === +today) {
    return "last_30_days"
  }

  const monthStart = today.startOf("month")
  const monthEnd = today.endOf("month").startOf("day")
  if (+fromNorm === +monthStart && +toNorm === +monthEnd) {
    return "this_month"
  }

  const lastMonth = today.minus({ months: 1 })
  const lastMonthStart = lastMonth.startOf("month")
  const lastMonthEnd = lastMonth.endOf("month").startOf("day")
  if (+fromNorm === +lastMonthStart && +toNorm === +lastMonthEnd) {
    return "last_month"
  }

  return "custom_range"
}

export const getDateFilterLabel = (dateFilter: DateFilterValue): string => {
  const today = DateTime.now()

  if (dateFilter.rangeType === "from") {
    const fromDate = dateFilter.from ? DateTime.fromISO(dateFilter.from) : today
    return i18n.t("app.reservations_list.date_filter.range_type.from") + " " + fromDate.toFormat("MMM d, yyyy")
  }

  if (dateFilter.rangeType === "to") {
    const toDate = dateFilter.to ? DateTime.fromISO(dateFilter.to) : today
    return i18n.t("app.reservations_list.date_filter.range_type.to") + " " + toDate.toFormat("MMM d, yyyy")
  }

  if (dateFilter.rangeType === "range" && dateFilter.from && dateFilter.to) {
    const from = DateTime.fromISO(dateFilter.from)
    const to = DateTime.fromISO(dateFilter.to)
    const preset = detectPreset(from, to)

    if (preset !== "custom_range") {
      return i18n.t(`app.reservations_list.date_filter.presets.${preset}`)
    }

    return `${from.toFormat("MMM d, yyyy")} - ${to.toFormat("MMM d, yyyy")}`
  }

  return ""
}
